
  import { computed, defineComponent, ref, watch } from 'vue';
  import { Button } from '@voximplant/spaceui';
  import ActiveCall from '@/components/calls/ActiveCall.vue';
  import { useI18n } from 'vue-i18n';
  import { useStore } from 'effector-vue/composition';
  import { $settings, changeMute, toggleFullScreen } from '@/store/settings';
  import {
    hangUp,
    toggleLocalVideo,
    toggleRemoteSharingVideo,
    currentSelectCall,
    $calls,
    toggleCallStatus,
  } from '@/store/calls';
  import { changeVideoParam } from '@/lib/sdkSource';
  import { changeComponent, changeComponentDialingStatus } from '@/store/components';
  import { needToShowAdditionalButton } from '@/store/NeedToShowAdditionalButton';

  export default defineComponent({
    name: 'VideoCallFooter',
    components: {
      ActiveCall,
      Button,
    },
    props: {
      isMaximize: {
        type: Boolean,
        default: false,
      },
    },
    setup() {
      const { t } = useI18n();
      const settings = useStore($settings);
      const allCalls = useStore($calls);
      const selectCall = useStore(currentSelectCall);
      const showAdditionalButton = useStore(needToShowAdditionalButton);

      // classes
      const getFullscreenBtnClasses = computed(() => ({
        fullscreen: settings.value.fullscreen,
        button: !settings.value.fullscreen,
      }));
      const getAudioBtnClasses = computed(() => ({
        ...getFullscreenBtnClasses.value,
        'fullscreen-media-disable': settings.value.fullscreen && settings.value.mute,
      }));
      const getVideoBtnClasses = computed(() => ({
        ...getFullscreenBtnClasses.value,
        'fullscreen-media-disable': settings.value.fullscreen && settings.value.videoMute,
        'fullscreen-disabled': settings.value.sharing || isCallProgressing.value,
      }));

      // flags
      const canCurrentCallToggleActive = computed(() => {
        const selectCallId = selectCall.value?.id;
        if (!selectCallId) return false;
        return allCalls.value[selectCallId]?.params.canToggleActive;
      });
      const isCallPaused = computed(
        () =>
          !selectCall.value?.active ||
          allCalls.value[selectCall.value?.id]?.params.remotePausedState
      );
      const isCallProgressing = computed(
        () => selectCall.value?.status === 'PROGRESSING' || selectCall.value?.status === 'ALERTING'
      );

      // hide footer for full screen mode when user be inactive
      const showFooter = ref(true);
      const SHOW_FOOTER_TIMER = 3000; // 3 second
      let isTimerEnable = false;
      let timer: ReturnType<typeof setTimeout>;
      const changeShowFooter = (param: boolean) => {
        showFooter.value = param;
      };
      const resetTimer = () => {
        if (!showFooter.value) changeShowFooter(true);
        clearTimeout(timer);
        timer = setTimeout(() => changeShowFooter(false), SHOW_FOOTER_TIMER);
      };
      const watchUserActive = () => {
        document.addEventListener('keydown', resetTimer);
        document.addEventListener('mouseup', resetTimer);
        document.addEventListener('mousemove', resetTimer);
      };
      const resetWatcher = () => {
        document.removeEventListener('keydown', resetTimer);
        document.removeEventListener('mouseup', resetTimer);
        document.removeEventListener('mousemove', resetTimer);
      };
      watch(settings, () => {
        if (settings.value.fullscreen && !isTimerEnable) {
          watchUserActive();
          isTimerEnable = true;
        } else if (!settings.value.fullscreen && isTimerEnable) {
          if (!showFooter.value) changeShowFooter(true);
          clearTimeout(timer);
          resetWatcher();
          isTimerEnable = false;
        }
      });

      const toggleVideo = () => {
        if (selectCall.value) {
          toggleLocalVideo({ id: selectCall.value?.id, status: !!settings.value.videoMute });
          changeVideoParam(!!settings.value.videoMute);
        }
      };
      const toggleSharing = () => {
        if (selectCall.value) {
          toggleRemoteSharingVideo({ id: selectCall.value?.id, status: !settings.value.sharing });
        }
      };
      const makeNewCall = () => {
        changeComponent('Dialing');
        changeComponentDialingStatus('newCall');
      };

      // getters
      const getIconColor = (disabled: boolean, video: boolean) => {
        if (disabled) return '--sui-gray-400';
        else
          return settings.value[video ? 'videoMute' : 'mute'] ? '--sui-red-500' : '--sui-gray-700';
      };
      const getMuteIconColor = computed(() => {
        if (isCallPaused.value || isCallProgressing.value) return; // for default spaceUI disabled color
        return settings.value.fullscreen ? '--sui-white' : '--sui-gray-700';
      });

      return {
        t,
        settings,
        isCallPaused,
        selectCall,
        showAdditionalButton,
        toggleVideo,
        hangUp,
        changeMute,
        makeNewCall,
        changeComponent,
        changeComponentDialingStatus,
        toggleSharing,
        toggleFullScreen,
        showFooter,
        canCurrentCallToggleActive,
        isCallProgressing,
        getIconColor,
        toggleCallStatus,
        getMuteIconColor,
        getAudioBtnClasses,
        getVideoBtnClasses,
        getFullscreenBtnClasses,
      };
    },
  });
